import { createSpotSwapFilters } from '@/filters/spotSwapFilters'
import { createSwapFilters } from '@/filters/swapFilters'
import type { useSwapAlertsStore } from '@/stores/swapAlertsStore'
import { split, keysToSnake, strToNumber, parseCommaSeparatedAssets } from '@/utils'
import { AVAILABLE_EXCHANGES_LIST } from '@/utils/consts'
import { extractFormData, extractZodFromForm } from '@/utils/forms'
import { ref } from 'vue'
import z from 'zod'

export function useSwapFilters(store: ReturnType<typeof useSwapAlertsStore>, marketType: string) {
  const isSwap = marketType === 'swap'
  const isSwapSpot = marketType === 'swap-spot'
  const isSpotSwap = marketType === 'spot-swap'
  const isSpotSpot = marketType === 'spot'
  const useFundingFilters = isSwap || isSwapSpot

  const filters = useFundingFilters ? createSwapFilters() : createSpotSwapFilters()
  const initValues = extractFormData(filters)
  const formData = ref({ ...initValues })
  const zodForm = z.object(extractZodFromForm(filters))

  const loadFiltersFromSettings = (settings: Record<string, string>) => {
    const cachedFilters = {
      exsBuy: null as string[] | null,
      exsSell: null as string[] | null,
      symbols: null as string | null,
      spreadMin: null as number | null,
      excludeSelectedSymbols: null as boolean | null,
      fundingMin: null as number | null,
      vol24hLongMin: null as number | null,
      vol24hShortMin: null as number | null,
      maxAgeSeconds: null as number | null,
    }
    if (isSpotSwap) {
      cachedFilters.exsBuy = split(settings?.spot_futures_filter_exs_buy)
      cachedFilters.exsSell = split(settings?.spot_futures_filter_exs_sell)
      cachedFilters.symbols = settings?.spot_futures_filter_symbols ?? null
      cachedFilters.spreadMin = strToNumber(settings?.spot_futures_filter_spread_min)
      cachedFilters.excludeSelectedSymbols =
        strToNumber(settings?.spot_futures_filter_exclude_symbols, 0) == 1

      cachedFilters.vol24hLongMin = strToNumber(settings?.spot_futures_filter_vol24h_long_min)
      cachedFilters.vol24hShortMin = strToNumber(settings?.spot_futures_filter_vol24h_short_min)
      cachedFilters.maxAgeSeconds = strToNumber(settings?.spot_futures_filter_max_age_seconds)
    } else if (isSpotSpot) {
      cachedFilters.exsBuy = split(settings?.spot_spot_filter_exs_buy)
      cachedFilters.exsSell = split(settings?.spot_spot_filter_exs_sell)
      cachedFilters.symbols = settings?.spot_spot_filter_symbols ?? null
      cachedFilters.spreadMin = strToNumber(settings?.spot_spot_filter_spread_min)
      cachedFilters.excludeSelectedSymbols =
        strToNumber(settings?.spot_spot_filter_exclude_symbols, 0) == 1

      cachedFilters.vol24hLongMin = strToNumber(settings?.spot_spot_filter_vol24h_long_min)
      cachedFilters.vol24hShortMin = strToNumber(settings?.spot_spot_filter_vol24h_short_min)
      cachedFilters.maxAgeSeconds = strToNumber(settings?.spot_spot_filter_max_age_seconds)
    } else if (isSwapSpot) {
      cachedFilters.exsBuy = split(settings?.futures_spot_filter_exs_buy)
      cachedFilters.exsSell = split(settings?.futures_spot_filter_exs_sell)
      cachedFilters.symbols = settings?.futures_spot_filter_symbols ?? null
      cachedFilters.spreadMin = strToNumber(settings?.futures_spot_filter_spread_min)
      cachedFilters.fundingMin = strToNumber(settings?.futures_spot_filter_funding_min)
      cachedFilters.excludeSelectedSymbols =
        strToNumber(settings?.futures_spot_filter_exclude_symbols, 0) == 1

      cachedFilters.vol24hLongMin = strToNumber(settings?.futures_spot_filter_vol24h_long_min)
      cachedFilters.vol24hShortMin = strToNumber(settings?.futures_spot_filter_vol24h_short_min)
      cachedFilters.maxAgeSeconds = strToNumber(settings?.futures_spot_filter_max_age_seconds)
    } else {
      cachedFilters.exsBuy = split(settings?.futures_futures_filter_exs_buy)
      cachedFilters.exsSell = split(settings?.futures_futures_filter_exs_sell)
      cachedFilters.symbols = settings?.futures_futures_filter_symbols ?? null
      cachedFilters.spreadMin = strToNumber(settings?.futures_futures_filter_spread_min)
      cachedFilters.fundingMin = strToNumber(settings?.futures_futures_filter_funding_min)
      cachedFilters.excludeSelectedSymbols =
        strToNumber(settings?.futures_futures_filter_exclude_symbols, 0) == 1

      cachedFilters.vol24hLongMin = strToNumber(settings?.futures_futures_filter_vol24h_long_min)
      cachedFilters.vol24hShortMin = strToNumber(settings?.futures_futures_filter_vol24h_short_min)
      cachedFilters.maxAgeSeconds = strToNumber(settings?.futures_futures_filter_max_age_seconds)
    }
    cachedFilters.exsBuy = cachedFilters.exsBuy.filter((x: string) =>
      AVAILABLE_EXCHANGES_LIST.includes(x),
    )
    cachedFilters.exsSell = cachedFilters.exsSell.filter((x: string) =>
      AVAILABLE_EXCHANGES_LIST.includes(x),
    )
    formData.value = {
      ...formData.value,
      ...cachedFilters,
    }
    const snaked = keysToSnake(cachedFilters) as Record<string, any>
    if (typeof snaked.symbols === 'string') {
      snaked.symbols = parseCommaSeparatedAssets(snaked.symbols)
    }
    store.filters = {
      ...store.filters,
      ...snaked,
    }
  }

  return { formData, zodForm, loadFiltersFromSettings, filters }
}
